import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "../../common/components/Grid";
import ReactForm from "../../common/components/ReactForm";
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { canInsertResource, canRemoveResource, insertResource, removeResource, useAllResourceDefs } from "../../reducers/crewResourceSlice";
import GroupedSettings from "./GroupedSettings";
import PropTypes from "prop-types";

const defaultSort = ['resource'];

const newResourceTemplate = {};

const ResourceDetails = forwardRef(({ value: resource, groupCtrl, disabled = false, onChange }, formRef) => {
    const { t } = useTranslation();
    return <ReactForm ref={formRef}>
        <Row>
            <Col>
                <TextInput placeholder={t('name')} readonly={resource.internalid} value={resource.resource} onChange={value => onChange('resource')(value)} disabled={disabled} required></TextInput>
            </Col>
        </Row>
        {groupCtrl}
    </ReactForm>
});
ResourceDetails.propTypes = {
    value: PropTypes.object,
    groupCtrl: PropTypes.any,
    disabled: PropTypes.bool,
    onChange: PropTypes.func
}

const CrewResources = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const resources = useAllResourceDefs();
    const sortFields = useMemo(() => ({
        resource: t('name')
    }), [t]);
    const groupingCtrls = useMemo(() => {
        const ctrls = [{ type: 'type', label: t('type', { count: 1 }), title: t('type', { count: 1 }), field: ['site','level','type'], sortfield: 'type', wildcard: t('anyWildcard'), strict: false }]
        if (clientConfig.enableLevels) {
            ctrls.unshift({ type: 'level', label: t('level', { count: 1 }), title: t('level', { count: 1 }), field: ['site','level','type'], sortfield: 'level', wildcard: t('anyWildcard'), strict: false });
        }
        if (clientConfig.enableSites) {
            ctrls.unshift({ type: 'site', label: t('site', { count: 1 }), title: t('site', { count: 1 }), field: ['site','level','type'], sortfield: 'site', wildcard: t('anyWildcard'), strict: false });
        }
        return ctrls;
    }, [clientConfig.enableLevels, clientConfig.enableSites, t]);
    const onAdd = useCallback(resource => {
        delete resource.internalid;
        return insertResource(network, resource)
    }, [network]);
    const onRemove = useCallback(resource => {
        return removeResource(network, resource)
    }, [network]);

    return <GroupedSettings Details={ResourceDetails} data={resources} uuid={uuid} title={label}
            defaultEntry={newResourceTemplate} defaultSort={defaultSort} sortFields={sortFields}
            onClose={onClose} makeLabel={e => e.resource}
            initialGroupingField="resource"
            groupingExclusions={['site', 'level', 'type', 'internalid']}
            canAdd={entry => canInsertResource(credentials, entry)} canRemove={entry => canRemoveResource(credentials, entry)}
            onAdd={onAdd} onRemove={onRemove}
            groupingCtrls={groupingCtrls}></GroupedSettings>
}
CrewResources.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default CrewResources;